// AdminIntegrations — Click-and-play integrations hub.
// PMR shops can enable the CMMS + property-management + accounting +
// comms tools they already use. Field Radar becomes the layer that
// connects everything they already run — signup flow uses the same grid.
import { useEffect, useMemo, useState } from "react";
import {
  PlugsConnected, Copy, CheckCircle, Warning, Clipboard,
  Plus, X, LinkSimple,
} from "@phosphor-icons/react";
import { AdminShell as Layout } from "@/components/Layout";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminIntegrations() {
  const [data, setData] = useState(null);
  const [openVendor, setOpenVendor] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(null);

  async function load() {
    try {
      const r = await api.get("/integrations");
      setData(r.data);
    } catch { toast.error("Load failed"); }
  }
  useEffect(() => { load(); }, []);

  const byCategory = useMemo(() => {
    if (!data) return {};
    const out = {};
    (data.categories || []).forEach(c => { out[c.key] = []; });
    (data.integrations || []).forEach(i => {
      (out[i.category] = out[i.category] || []).push(i);
    });
    return out;
  }, [data]);

  const activeCount = (data?.integrations || []).filter(i => i.active).length;
  const liveCount = (data?.integrations || []).filter(i => i.status === "live").length;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="admin-integrations">
        <div className="mb-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Connect your stack</div>
          <h1 className="text-2xl font-black mt-1">Integrations Hub</h1>
          <p className="text-sm text-slate-500 mt-1">
            Field Radar plays nice with the tools you already use. Enable what you have — WOs flow in automatically.
          </p>
        </div>

        {/* Intake email banner */}
        {data && (
          <div className="mb-6 bg-cyan-50 border-2 border-cyan-200 rounded-2xl p-4" data-testid="intake-banner">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Your inbound intake</div>
                <div className="font-black text-slate-900 mt-1">
                  Forward WO emails to <span className="font-mono">{data.intake_email}</span>{" "}
                  with subject <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-cyan-200">{data.tenant_tag}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">{data.intake_instructions}</div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(`${data.intake_email}`); toast.success("Copied"); }}
                className="shrink-0 h-9 px-3 bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"
                data-testid="intake-copy-email">
                <Copy size={12}/> Copy email
              </button>
              <button onClick={() => { navigator.clipboard.writeText(data.tenant_tag); toast.success("Copied"); }}
                className="shrink-0 h-9 px-3 bg-white border-2 border-slate-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"
                data-testid="intake-copy-tag">
                <Copy size={12}/> Copy tag
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat n={activeCount} label="Active" color="text-emerald-600"/>
            <Stat n={liveCount} label="Available" color="text-cyan-600"/>
            <Stat n={data.integrations?.length || 0} label="Total supported" color="text-slate-900"/>
            <Stat n={(data.integrations || []).filter(i => i.status === "soon").length} label="Coming soon" color="text-fuchsia-600"/>
          </div>
        )}

        {/* Grid by category */}
        {data && (data.categories || []).map(cat => (
          <section key={cat.key} className="mb-8" data-testid={`integrations-cat-${cat.key}`}>
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">{cat.label}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(byCategory[cat.key] || []).map(i => (
                <IntegrationCard key={i.key} i={i}
                  onOpen={() => setOpenVendor(i)}
                  onPaste={() => setPasteOpen(i)}/>
              ))}
            </div>
          </section>
        ))}

        {!data && <div className="text-sm text-slate-400">Loading…</div>}
      </div>

      {openVendor && <ConfigureModal i={openVendor} onClose={() => { setOpenVendor(null); load(); }} intakeEmail={data?.intake_email} tenantTag={data?.tenant_tag}/>}
      {pasteOpen && <PasteModal i={pasteOpen} onClose={() => setPasteOpen(null)}/>}
    </Layout>
  );
}

function Stat({ n, label, color }) {
  return (
    <div className="bg-white rounded-xl p-3 shadow-sm">
      <div className={`text-2xl font-black ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </div>
  );
}

function IntegrationCard({ i, onOpen, onPaste }) {
  const isLive = i.status === "live";
  const isSoon = i.status === "soon";
  return (
    <div className={`relative bg-white rounded-xl p-4 shadow-sm border-2 transition ${i.active ? "border-emerald-400" : "border-transparent hover:border-cyan-300"}`}
      data-testid={`integration-card-${i.key}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm shrink-0 ${
          i.category === "cmms" ? "bg-cyan-100 text-cyan-700"
          : i.category === "pms" ? "bg-fuchsia-100 text-fuchsia-700"
          : i.category === "accounting" ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-100 text-slate-700"
        }`}>
          {i.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-black text-slate-900">{i.name}</div>
            {i.active && <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Active</span>}
            {isSoon && <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded">Coming soon</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{i.tagline}</div>
          {i.active && i.stats?.inbound_count > 0 && (
            <div className="text-[10px] text-emerald-600 font-bold mt-1">
              {i.stats.inbound_count} WO{i.stats.inbound_count === 1 ? "" : "s"} received
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-1">
            {isLive && (
              <button onClick={onOpen}
                className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest rounded ${i.active ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-cyan-600 hover:bg-cyan-700 text-white"}`}
                data-testid={`integration-configure-${i.key}`}>
                {i.active ? "Configure" : "Enable"}
              </button>
            )}
            {isLive && i.intake_supported && (
              <button onClick={onPaste}
                className="h-8 px-3 text-[10px] font-black uppercase tracking-widest rounded bg-white border border-slate-300 hover:bg-slate-50 text-slate-700"
                data-testid={`integration-paste-${i.key}`}>
                <Clipboard size={12} className="inline mr-1"/> Paste WO
              </button>
            )}
            {isSoon && (
              <button onClick={onOpen}
                className="h-8 px-3 text-[10px] font-black uppercase tracking-widest rounded bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800"
                data-testid={`integration-request-${i.key}`}>
                Request access
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigureModal({ i, onClose, intakeEmail, tenantTag }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isSoon = i.status === "soon";

  async function submit() {
    setBusy(true);
    try {
      if (isSoon) {
        const r = await api.post(`/integrations/${i.key}/interest`, { notes });
        toast.success(r.data.message || "Got it — we'll be in touch");
      } else {
        await api.post(`/integrations/${i.key}/enable`, { active: true, notes });
        toast.success(`${i.name} enabled`);
      }
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  async function disable() {
    if (!confirm(`Disable ${i.name}?`)) return;
    try {
      await api.post(`/integrations/${i.key}/disable`);
      toast.success("Disabled");
      onClose();
    } catch { toast.error("Failed"); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="integration-modal">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">{i.category}</div>
            <h3 className="font-black text-xl mt-1">{i.name}</h3>
          </div>
          <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600"/></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">{i.tagline}</p>

        {isSoon ? (
          <>
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-xl p-3 mb-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 mb-1">Coming soon</div>
              <div className="text-sm text-slate-700">
                Tap "Request access" and we'll ping you when it's live. Meanwhile, most CMMS emails work today via the shared intake below.
              </div>
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="(optional) Anything specific you'd want it to do?"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="integration-interest-notes"/>
          </>
        ) : (
          <>
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 mb-1">How this works</div>
              <div className="text-sm text-slate-700">
                1. In {i.name}, add <span className="font-mono">{intakeEmail}</span> as a notification address (or set up an email forward).
                <br/>2. Every new WO email that hits our intake with subject tag <span className="font-mono">{tenantTag}</span> lands in your Dispatch board.
                <br/>3. Prefer to skip forwarding? Use "Paste WO" any time — Claude parses the raw text into a WO.
              </div>
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="(optional) Notes on your setup — e.g. account manager name"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2"/>
            {i.active && (
              <div className="text-[10px] text-slate-500">
                Stats: {i.stats?.inbound_count || 0} WOs received · last sync {i.last_sync_at?.slice(0, 16).replace("T", " ") || "never"}
              </div>
            )}
          </>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
          {i.active && !isSoon && (
            <button onClick={disable} className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg py-2.5 font-black text-sm" data-testid="integration-disable">Disable</button>
          )}
          <button onClick={submit} disabled={busy}
            className={`flex-1 rounded-lg py-2.5 font-black text-sm text-white disabled:opacity-50 ${isSoon ? "bg-fuchsia-600 hover:bg-fuchsia-700" : "bg-cyan-600 hover:bg-cyan-700"}`}
            data-testid="integration-submit">
            {busy ? "…" : (isSoon ? "Request access" : (i.active ? "Save" : "Enable"))}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasteModal({ i, onClose }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function submit() {
    if (raw.trim().length < 20) { toast.error("Paste the whole email/text"); return; }
    setBusy(true);
    try {
      const r = await api.post("/integrations/intake/paste", { vendor_key: i.key, raw });
      setResult(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Parse failed")); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="paste-modal">
        {!result ? (
          <>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Paste WO from {i.name}</div>
            <h3 className="font-black text-xl mt-1">Import work order</h3>
            <p className="text-xs text-slate-500 mt-1 mb-3">Copy the email body from {i.name} and paste below — Claude extracts the fields.</p>
            <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={12}
              placeholder="Paste the full email content here…"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" data-testid="paste-raw"/>
            <div className="flex gap-2 mt-3">
              <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
              <button onClick={submit} disabled={busy}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50"
                data-testid="paste-submit">
                {busy ? "Parsing…" : "Parse + Create WO"}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="paste-result">
            <CheckCircle weight="fill" size={40} className="text-emerald-500 mb-2"/>
            <h3 className="font-black text-xl">WO created</h3>
            <div className="mt-3 space-y-1 text-sm">
              <div><span className="text-slate-500">Title:</span> {result.parsed.title}</div>
              <div><span className="text-slate-500">Address:</span> {result.parsed.address || "—"} {result.parsed.unit ? `#${result.parsed.unit}` : ""}</div>
              <div><span className="text-slate-500">Priority:</span> {result.parsed.priority}</div>
              <div><span className="text-slate-500">Trade:</span> {result.parsed.trade}</div>
              {result.parsed.external_wo_number && <div><span className="text-slate-500">External WO #:</span> {result.parsed.external_wo_number}</div>}
            </div>
            <div className="flex gap-2 mt-4">
              <a href={`/admin/jobs/${result.wo_id}`} className="flex-1 bg-slate-900 hover:bg-black text-white rounded-lg py-2.5 font-black text-sm text-center">Open WO →</a>
              <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
